import { useState } from 'react';
import { Flex, Loader } from '@mantine/core';
import { usePaginatedDebitsByPatient } from 'api/resource';
import { TransactionColumns } from 'columns/Transaction';
import { BillingDataGridWrapper } from 'components/common';
import { Debit, Healthcard, Patient, Payor } from 'interfaces';
import {
  MRT_ColumnDef,
  MRT_ShowHideColumnsButton,
  MRT_TableOptions,
} from 'mantine-react-table';
import { formatPatientName } from '@billing/validation';
import { navigateToPath } from 'utils/miscUtils';

const defaultPagination = {
  pageIndex: 0,
  pageSize: 10,
};

interface PatientPaymentHistoryTableProps {
  patientID: string;
  payors: Payor[];
  patient?: Pick<Patient, 'firstName' | 'lastName'>;
  healthcard?: Healthcard | null;
}

export const PatientPaymentHistoryTable: React.FC<
  PatientPaymentHistoryTableProps
> = ({ patientID, payors, patient }) => {
  const patientName = formatPatientName(
    patient?.firstName ?? '',
    patient?.lastName ?? ''
  );

  const title = `${patientName ? `${patientName} ` : ''}Payment History`;
  const [pagination, setPagination] = useState(defaultPagination);
  const { data, isLoading, isFetching } = usePaginatedDebitsByPatient({
    patientID,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });

  const columns: MRT_ColumnDef<Debit>[] = TransactionColumns({
    payors,
  });

  const tableOptions: MRT_TableOptions<Debit> = {
    data: data?.data || [],
    columns,
    layoutMode: 'semantic',
    initialState: {
      pagination: defaultPagination,
      columnVisibility: {
        Patient: false,
        'Overhead Paid': false,
        DOB: false,
        'Paid To Provider': false,
      },
    },
    state: {
      pagination,
      showSkeletons: isLoading || isFetching,
    },
    renderToolbarInternalActions: ({ table }) => (
      <MRT_ShowHideColumnsButton table={table} />
    ),
    rowCount: data?.total ?? 0,
    manualPagination: true,
    onPaginationChange: setPagination,
    mantineTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => {
        navigateToPath({ path: `/claims/edit/${row.original.claimID}` });
      },
    }),
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="95vh">
        <Loader size="xl" type="bars" />
      </Flex>
    );
  }

  return (
    <BillingDataGridWrapper
      title={title}
      tableOptions={tableOptions}
      dataTestID="patient-payment-history"
      showAlertBanner={false}
    />
  );
};
